import { useState, type ReactNode } from "react";
import {
  SlidersHorizontal,
  Users,
  Tag,
  Paperclip,
  MessageCircle,
  CircleDot,
  AlertCircle,
  Plus,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import {
  FilterOptionRow,
  FilterPanelBody,
  FilterPanelContent,
  FilterPanelEmpty,
  FilterPanelHeader,
  FilterSection,
  FilterSectionNote,
  FilterSections,
  FilterSwitchRow,
} from "@/components/filters/FilterPanel";
import { matchesFilterQuery } from "@/components/filters/matchesFilterQuery";
import { AssigneeAvatar } from "@/domains/pm/components/shared/AssigneeAvatar";
import { StatusBadge } from "@/domains/pm/components/shared/StatusBadge";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { doneStatusKeys, visibleStatuses } from "@/domains/pm/statuses/statusCatalogue";
import { getDisciplineName } from "@/domains/pm/utils/disciplineName";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { CONTROL_ACTIVE_CLASS } from "@/components/ui/control-scale";
import { cn } from "@/lib/utils";
import type { IssueFilterDimension, IssueFilterValue } from "./issueFilters";
import type { Discipline, IssueStatus, UserProfile } from "@/domains/pm/types";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

type Dimension = IssueFilterDimension;

// Status leads — it is the attribute users reach for first, and it led the
// header strip these dimensions replaced.
//
// The list splits in two. The first three pick from a set and need a panel;
// the last three are single yes/no predicates, so they are switches sitting
// directly in the list — a drill-down to flip one flag was two clicks and a
// pane for something the row can say by itself. Mirrors "Verified only" on
// the documents filter, which is the same shape.
type PanelDimension = Extract<Dimension, "status" | "assignee" | "discipline">;
const PANEL_DIMENSIONS: PanelDimension[] = ["status", "assignee", "discipline"];
const SWITCH_DIMENSIONS: Dimension[] = ["documents", "comments", "overdue"];
const DIMENSIONS: Dimension[] = [...PANEL_DIMENSIONS, ...SWITCH_DIMENSIONS];

// Attribute icons — matched to the issue context menu / Group By list.
const DIMENSION_ICON: Record<Dimension, LucideIcon> = {
  status: CircleDot,
  overdue: AlertCircle,
  assignee: Users,
  discipline: Tag,
  documents: Paperclip,
  comments: MessageCircle,
};

interface IssueFilterControlProps {
  users: UserProfile[];
  disciplines: Discipline[];
  value: IssueFilterValue;
  onChange: (next: IssueFilterValue) => void;
  /**
   * Dimensions this host does not offer. "My Issues" hides `assignee` (its
   * tabs already lock the person-relationship) and `overdue` (it owns that
   * predicate through its own Overdue tab); two controls for one predicate
   * would fight. Generalised from the original `hideAssignee`.
   */
  hiddenDimensions?: Dimension[];
  /** Scope-wide per-status totals shown against the status rows. */
  statusCounts?: Record<IssueStatus, number>;
  /** The project in scope, or null for "All projects". Decides which custom
   *  statuses appear. */
  buildingProjectId?: number | null;
  /** Opens the "New status" dialog. Supplied only when the page has a single
   *  project in scope and the user may manage it — the permission check and
   *  the dialogs live with the page, so this control stays renderable without
   *  a store provider. */
  onCreateStatus?: () => void;
  /** Scope-wide overdue total. Rendered red when non-zero — it is a warning,
   *  and it used to be a permanent red badge in the page header. */
  overdueCount?: number;
  /** How many dimensions are filtered — drives the badge and the active look. */
  activeCount: number;
  /** Popover state is owned by the toolbar so a chip click can reopen its dimension. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: Dimension | null;
  onStepChange: (step: Dimension | null) => void;
}

/**
 * Filter control for the PM issues list, built on the shared filter panel
 * (`components/filters/FilterPanel`, the /documents design). A "Filters" button
 * sits in the toolbar's right-hand cluster; opening it lists the dimensions,
 * each opening its options in place. `step` is the open section, so a chip
 * click lands on its dimension already expanded. The button
 * carries a count badge of the active dimensions; the chips themselves live in
 * `IssueFilterChips`, to the left of the cluster.
 *
 * The badge appears only once something is filtered — no reserved slot, so the
 * unfiltered button is just "Filters" with no dead space on its right. The
 * width it gains when the badge appears is taken from the chip row to its left
 * (the cluster is right-pinned), so the search box never moves.
 */
export function IssueFilterControl({
  users,
  disciplines,
  value,
  onChange,
  hiddenDimensions = [],
  statusCounts,
  buildingProjectId,
  onCreateStatus,
  overdueCount = 0,
  activeCount,
  open,
  onOpenChange,
  step: stepProp,
  onStepChange,
}: IssueFilterControlProps) {
  const { t, language } = useLanguage();
  const { catalogue: statusCatalogue } = useStatusCatalogue();
  const f = t.pm.issues.filterBy;
  const setOpen = onOpenChange;
  const setStep = onStepChange;

  // `step` is caller-controlled, so the picker must not trust it. A hidden
  // dimension has no business showing a panel, and a switch dimension no
  // longer HAS one — a chip click routes here for both, and either would
  // otherwise render an empty popover.
  const step =
    stepProp &&
    !hiddenDimensions.includes(stepProp) &&
    (PANEL_DIMENSIONS as Dimension[]).includes(stepProp)
      ? stepProp
      : null;

  const dimensions = DIMENSIONS.filter((d) => !hiddenDimensions.includes(d));

  // A switch row is labelled by what switching it ON does, not by the
  // attribute it filters — "Has comments" rather than "Comments", which on a
  // toggle would read as a category with no stated direction.
  const dimensionLabel = (d: Dimension): string =>
    d === "status"
      ? f.status
      : d === "overdue"
        ? f.overdueOnly
        : d === "assignee"
          ? f.assignee
          : d === "discipline"
            ? f.discipline
            : d === "documents"
              ? f.hasDocuments
              : f.hasComments;

  /**
   * Overdue pins status to in_progress at QUERY time (`toPMIssueFilters`), so
   * this only flips the flag — it must NOT prune `value.status`. Pruning made
   * the toggle one-way: switching overdue back off could not restore statuses
   * the toggle had already deleted. The pinning stays visible instead: the
   * status picker disables the two statuses overdue rules out and says why,
   * and the chips drop the status dimension while overdue is on so nothing
   * advertises a selection the query is overriding.
   */
  const pickOverdue = (next: boolean) => onChange({ ...value, overdueOnly: next || undefined });

  /**
   * The switch dimensions set the affirmative predicate or clear it —
   * `undefined`, never `false`. The repository still understands `false`
   * ("issues with NO linked documents"); the UI simply no longer offers it,
   * because a switch has nowhere to put a third state and the negative was
   * the rarely-wanted half.
   */
  const setFlag = (key: "hasLinkedDocuments" | "hasComments", on: boolean) =>
    onChange({ ...value, [key]: on || undefined });

  const switchState = (d: Dimension): { on: boolean; toggle: (on: boolean) => void } =>
    d === "documents"
      ? { on: value.hasLinkedDocuments === true, toggle: (on) => setFlag("hasLinkedDocuments", on) }
      : d === "comments"
        ? { on: value.hasComments === true, toggle: (on) => setFlag("hasComments", on) }
        : { on: value.overdueOnly === true, toggle: pickOverdue };

  const [query, setQuery] = useState("");
  const searching = query.trim().length > 0;

  // The three set dimensions as data, so the accordion and the search results
  // render the same rows from one source.
  const statusDescriptors = visibleStatuses(statusCatalogue, buildingProjectId);
  const pinnedOut = value.overdueOnly ? doneStatusKeys(statusCatalogue, buildingProjectId) : [];
  const sortedDisciplines = [...disciplines].sort((a, b) =>
    getDisciplineName(a, language).localeCompare(getDisciplineName(b, language), language)
  );
  const toggleIn = <T,>(list: T[], item: T): T[] =>
    list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

  const options: Record<PanelDimension, PanelOption[]> = {
    status: statusDescriptors.map(({ key, label }) => {
      const checked = value.status.includes(key);
      return {
        id: key,
        label,
        // A badge, not plain text: the status colour is how the list reads.
        display: <StatusBadge status={key} buildingProjectId={buildingProjectId} />,
        checked,
        // Only an UNCHECKED pinned-out row is locked; a checked one keeps its
        // handler so the selection can still be cleared.
        disabled: pinnedOut.includes(key) && !checked,
        dimmed: pinnedOut.includes(key),
        // A status the totals do not mention has none: show 0, never a blank,
        // which would read as "unknown" rather than "zero".
        count: statusCounts ? (statusCounts[key] ?? 0) : undefined,
        toggle: () => onChange({ ...value, status: toggleIn(value.status, key) }),
      };
    }),
    assignee: users.map((u) => ({
      id: String(u.id),
      label: userDisplayName(u),
      // Two people can share a display name; the email tells them apart.
      detail: u.email && u.email !== userDisplayName(u) ? u.email : undefined,
      leading: <AssigneeAvatar user={u} size={18} />,
      checked: value.assigneeUserIds.includes(u.id),
      toggle: () => onChange({ ...value, assigneeUserIds: toggleIn(value.assigneeUserIds, u.id) }),
    })),
    discipline: sortedDisciplines.map((d) => ({
      id: String(d.id),
      label: getDisciplineName(d, language),
      checked: value.disciplineIds.includes(d.id),
      toggle: () => onChange({ ...value, disciplineIds: toggleIn(value.disciplineIds, d.id) }),
    })),
  };

  const selectedCount = (d: PanelDimension): number =>
    d === "status"
      ? value.status.length
      : d === "assignee"
        ? value.assigneeUserIds.length
        : value.disciplineIds.length;
  const clearDimension = (d: PanelDimension) =>
    onChange(
      d === "status"
        ? { ...value, status: [] }
        : d === "assignee"
          ? { ...value, assigneeUserIds: [] }
          : { ...value, disciplineIds: [] }
    );
  const clearAll = () =>
    onChange({
      ...value,
      status: hiddenDimensions.includes("status") ? value.status : [],
      assigneeUserIds: hiddenDimensions.includes("assignee") ? value.assigneeUserIds : [],
      disciplineIds: hiddenDimensions.includes("discipline") ? value.disciplineIds : [],
      hasLinkedDocuments: hiddenDimensions.includes("documents")
        ? value.hasLinkedDocuments
        : undefined,
      hasComments: hiddenDimensions.includes("comments") ? value.hasComments : undefined,
      overdueOnly: hiddenDimensions.includes("overdue") ? value.overdueOnly : undefined,
    });

  const optionRow = (d: Dimension, o: PanelOption, inSearch = false) => (
    <FilterOptionRow
      key={`${d}-${o.id}`}
      label={inSearch ? o.label : (o.display ?? o.label)}
      ariaLabel={`${dimensionLabel(d)}: ${o.label}`}
      checked={o.checked}
      onToggle={o.toggle}
      disabled={o.disabled}
      className={o.dimmed && !o.disabled ? "opacity-50" : undefined}
      leading={o.leading}
      count={o.count}
      sublabel={inSearch ? [dimensionLabel(d), o.detail].filter(Boolean).join(" · ") : o.detail}
    />
  );

  const switchRow = (d: Dimension) => {
    const { on, toggle } = switchState(d);
    return (
      <FilterSwitchRow
        key={d}
        icon={DIMENSION_ICON[d]}
        label={dimensionLabel(d)}
        checked={on}
        onCheckedChange={toggle}
        adornment={
          d === "overdue" &&
          overdueCount > 0 && (
            // The overdue total used to be a permanent red badge in the page
            // header. It follows the dimension here so the warning is not
            // lost, red only when there is one.
            <span className="inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-[#fcebeb] px-1 text-[10px] font-semibold tabular-nums text-[#ef4129]">
              {overdueCount}
            </span>
          )
        }
      />
    );
  };

  const panelDimensions = PANEL_DIMENSIONS.filter((d) => dimensions.includes(d));
  const switchDimensions = SWITCH_DIMENSIONS.filter((d) => dimensions.includes(d));

  const sectionFooter = (d: Dimension) => {
    if (d === "status") {
      return (
        <>
          {value.overdueOnly && <FilterSectionNote>{f.overduePinsStatus}</FilterSectionNote>}
          {onCreateStatus ? (
            <button
              type="button"
              onClick={onCreateStatus}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" />
              {t.pm.issues.statuses.newStatus}
            </button>
          ) : buildingProjectId == null ? (
            <FilterSectionNote>{t.pm.issues.statuses.needsProject}</FilterSectionNote>
          ) : null}
        </>
      );
    }
    return null;
  };

  const emptyMessage = (d: Dimension) =>
    d === "assignee" ? t.pm.issues.noUsersFound : t.pm.issues.noDiscipline;

  const searchResults = () => {
    // A query naming a dimension ("status") lists all of its options, as a
    // query naming a switch ("overdue") shows that switch.
    const rows = panelDimensions.flatMap((d) => {
      // Two letters at least: one letter is in nearly every dimension name.
      const wholeDimension =
        query.trim().length >= 2 && matchesFilterQuery(dimensionLabel(d), query);
      return options[d]
        .filter((o) => wholeDimension || matchesFilterQuery(`${o.label} ${o.detail ?? ""}`, query))
        .map((o) => optionRow(d, o, true));
    });
    const switches = switchDimensions
      .filter((d) => matchesFilterQuery(dimensionLabel(d), query))
      .map(switchRow);
    if (rows.length === 0 && switches.length === 0) {
      return (
        <FilterPanelEmpty>
          {t.common.filterPanel.noMatches.replace("{query}", () => query.trim())}
        </FilterPanelEmpty>
      );
    }
    return (
      <FilterPanelBody className="py-1">
        {rows}
        {switches}
      </FilterPanelBody>
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setStep(null);
          setQuery("");
        }
      }}
      modal={true}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="control"
          aria-label={activeCount > 0 ? `${f.title} (${activeCount})` : f.title}
          className={cn("gap-1.5", activeCount > 0 && CONTROL_ACTIVE_CLASS)}
          {...pageInfoTarget("issues.filters")}
        >
          <SlidersHorizontal className="opacity-60" />
          {f.title}
          {activeCount > 0 && (
            <span
              aria-hidden="true"
              className="inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold tabular-nums text-primary-foreground"
            >
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <FilterPanelContent>
        <FilterPanelHeader
          title={t.common.filterPanel.active}
          activeCount={activeCount}
          clearLabel={t.common.filterPanel.clearAll}
          onClearAll={clearAll}
          search={{
            value: query,
            onChange: setQuery,
            placeholder: f.searchPlaceholder,
            clearLabel: t.common.filterPanel.clearSearch,
          }}
        />
        {searching ? (
          searchResults()
        ) : (
          <FilterPanelBody>
            <FilterSections value={step} onValueChange={(next) => setStep(next as Dimension)}>
              {panelDimensions.map((d) => (
                <FilterSection
                  key={d}
                  value={d}
                  icon={DIMENSION_ICON[d]}
                  label={dimensionLabel(d)}
                  selectedCount={selectedCount(d)}
                  onClear={() => clearDimension(d)}
                  clearLabel={t.common.filterPanel.clearAll}
                >
                  {options[d].length === 0 && d !== "status" ? (
                    <FilterSectionNote>{emptyMessage(d)}</FilterSectionNote>
                  ) : (
                    options[d].map((o) => optionRow(d, o))
                  )}
                  {sectionFooter(d)}
                </FilterSection>
              ))}
            </FilterSections>
            {switchDimensions.map(switchRow)}
          </FilterPanelBody>
        )}
      </FilterPanelContent>
    </Popover>
  );
}

interface PanelOption {
  id: string;
  /** Plain text — searched, and the checkbox's accessible name. */
  label: string;
  /** What the row shows in its section, when not the plain label. */
  display?: ReactNode;
  leading?: ReactNode;
  checked: boolean;
  disabled?: boolean;
  /** Greyed but still interactive (a ticked status overdue rules out). */
  dimmed?: boolean;
  count?: number;
  /** Secondary line under the label — an assignee's email. Also searched. */
  detail?: string;
  toggle: () => void;
}

function userDisplayName(u: UserProfile): string {
  return `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.email;
}
